import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { RecordLink } from '../../components/RecordLink'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { apiErrorMessage } from '../../lib/errors'
import { formatMoney, formatNumber } from '../../lib/format'
import type { TruckAvailability } from '../availability/api'
import { ComplianceBadge } from '../brokers/ComplianceBadge'
import { equipmentLabel } from '../loads/equipment'
import { getPostingMatches, type LoadMatch, type PostingMatches } from './api'
import { BrokerContact } from './BrokerContact'
import { availableDays, pickupDays, routeLine } from './describe'
import { cautionSentences, exclusionSentences, factorPhrases } from './reasons'
import { unitEquipment, unitName } from '../availability/unitName'

/**
 * The loads a posted unit could take, best first — and the open ones it could not, each
 * with every reason, so nothing leaves the list without saying why. Ranked and explained,
 * never decided: the dispatcher reads it and calls the broker.
 */
export function PostingMatchesPage() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()

  const [matches, setMatches] = useState<PostingMatches | null>(null)
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)

  useEffect(() => {
    if (id === undefined) {
      return
    }

    let active = true
    setMatches(null)
    setFailure(null)

    getPostingMatches(id)
      .then((found) => {
        if (active) {
          setMatches(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure({ error: caught })
        }
      })

    return () => {
      active = false
    }
  }, [id])

  if (failure !== null) {
    return (
      <section className="page">
        <Breadcrumbs
          trail={[
            { label: t('nav.availability'), to: '/availability' },
            { label: null, to: `/availability/${id}` },
            { label: t('matching.openLoads') },
          ]}
        />
        <p className="alert" role="alert">
          {apiErrorMessage(failure.error, t, 'matching.loadError')}
        </p>
      </section>
    )
  }

  if (matches === null) {
    return <p className="session-check">{t('matching.loading')}</p>
  }

  const { posting } = matches
  const open = posting.standing === 'Open'
  const language = i18n.language

  return (
    <section className="page">
      <Breadcrumbs
        trail={[
          { label: t('nav.availability'), to: '/availability' },
          { label: posting.reference, to: `/availability/${posting.id}` },
          { label: t('matching.openLoads') },
        ]}
      />
      <header className="page__header page__header--row">
        <div>
          <h1>
            {t('matching.loadsTitle', {
              unit: unitName(posting, t),
            })}
          </h1>
          <p className="page__subtitle">{t('matching.loadsSubtitle')}</p>
        </div>
        <Link className="button button--quiet" to={`/availability/${posting.id}`}>
          {t('matching.backToPosting')}
        </Link>
      </header>

      <p className="matches__subject">
        {[
          posting.carrierName,
          unitEquipment(posting, t),
          t(`availability.loadTypes.${posting.loadType}`),
          `${posting.city}, ${posting.state}`,
          availableDays(posting, t, language),
        ].join(' · ')}
      </p>

      {!open && <p className="placeholder">{t('matching.postingNotOpen')}</p>}

      {/* Said once, above the list, and the loads still ranked: the paperwork may be on
          its way (Lino, 2026-09-26; asked of Hugo on TRKC-31). */}
      {open && posting.heldBackBy.length > 0 && (
        <div className="alert alert--warning" role="status">
          <p>{t('matching.heldBack')}</p>
          {posting.heldBackBy.map((holdback) => (
            <p key={holdback.unit}>
              {t('availability.heldBackBy', {
                unit: t(`availability.units.${holdback.unit}`),
                name: holdback.culprit.name,
              })}{' '}
              <RecordLink subject={holdback.culprit} />
            </p>
          ))}
        </div>
      )}

      {open &&
        (matches.compatible.length === 0 ? (
          <p className="placeholder">{t('matching.noLoads')}</p>
        ) : (
          <ol className="matches" aria-label={t('matching.compatibleLoads')}>
            {matches.compatible.map((match) => (
              <LoadMatchCard key={match.load.id} match={match} />
            ))}
          </ol>
        ))}

      {open && matches.notCompatible.length > 0 && (
        <details className="matches__excluded">
          <summary>{t('matching.notCompatible', { count: matches.notCompatible.length })}</summary>
          <ul>
            {matches.notCompatible.map((match) => (
              <ExcludedLoad key={match.load.id} match={match} posting={posting} />
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function LoadMatchCard({ match }: { match: LoadMatch }) {
  const { t, i18n } = useTranslation()
  const { load } = match
  const language = i18n.language

  return (
    <li className="match">
      <p className="match__head">
        <Link to={`/loads/${load.id}`}>{routeLine(load, t)}</Link>
        <span className="muted"> · {pickupDays(load, t, language)}</span>
      </p>
      <p>
        {equipmentLabel(load, t)} ·{' '}
        {t('loads.book.freightLine', {
          loadType: t(`availability.loadTypes.${load.loadType}`),
          pounds: formatNumber(load.weightPounds, language),
        })}{' '}
        · {load.rateUsd === null ? t('loads.book.noRate') : formatMoney(load.rateUsd, 'USD', language)}
      </p>
      <p>
        {/* A company not in the book has no paperwork on file to show, and nothing held
            against it (TRKC-104): it says so where the badge would be. */}
        {load.brokerComplianceState === null ? (
          <>
            {load.brokerName} <span className="muted">· {t('loads.book.notInBook')}</span>
          </>
        ) : (
          <>
            <ComplianceBadge state={load.brokerComplianceState} /> {load.brokerName}
          </>
        )}
      </p>
      <p className="match__factors">
        {factorPhrases(match.factors, load.rateUsd, match.brokerVerified, t, language).join(' · ')}
      </p>
      {cautionSentences(match, load, t).map((sentence) => (
        <p key={sentence} className="alert alert--warning match__caution">
          {sentence}
        </p>
      ))}
      <BrokerContact load={load} brokerPhone={match.brokerPhone} brokerEmail={match.brokerEmail} />
    </li>
  )
}

function ExcludedLoad({ match, posting }: { match: LoadMatch; posting: TruckAvailability }) {
  const { t, i18n } = useTranslation()

  return (
    <li>
      <Link to={`/loads/${match.load.id}`}>{routeLine(match.load, t)}</Link>
      {' — '}
      {exclusionSentences(match, match.load, posting, t, i18n.language).join(' ')}
    </li>
  )
}
